import { useMemo, useState } from 'react';

import { PortfolioOverviewBuilding } from '@client/api';

export interface BuildingFilter {
  buildingFilter: string;
  setBuildingFilter: (value: string) => void;
  activeTags: string[];
  toggleTag: (tag: string) => void;
  /** All distinct tags across the buildings, sorted alphabetically. */
  allTags: string[];
  /** Buildings matching the current name search and selected tags. */
  filtered: PortfolioOverviewBuilding[];
}

/**
 * Shared building search + tag filter used by the portfolio reports. The name search is a
 * case-insensitive substring match; tags combine as OR (a building matches when it carries any
 * of the selected tags).
 */
export function useBuildingFilter(buildings: PortfolioOverviewBuilding[]): BuildingFilter {
  const [buildingFilter, setBuildingFilter] = useState('');
  const [activeTags, setActiveTags] = useState<string[]>([]);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    buildings.forEach((b) => (b.tags ?? []).forEach((tag) => set.add(tag)));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [buildings]);

  const filtered = useMemo(() => {
    const query = buildingFilter.trim().toLowerCase();
    return buildings.filter((b) => {
      const matchesName = query === '' || b.name.toLowerCase().includes(query);
      const matchesTags = activeTags.length === 0 || activeTags.some((tag) => (b.tags ?? []).includes(tag));
      return matchesName && matchesTags;
    });
  }, [buildings, buildingFilter, activeTags]);

  const toggleTag = (tag: string) =>
    setActiveTags((current) => (current.includes(tag) ? current.filter((x) => x !== tag) : [...current, tag]));

  return { buildingFilter, setBuildingFilter, activeTags, toggleTag, allTags, filtered };
}
